import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import axiosClient from '../api/axiosClient';
import { AxiosError } from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  CheckCircle,
  Error,
  Delete,
  InsertDriveFile,
  Settings,
  Warning,
} from '@mui/icons-material';
import { excelMappingApi } from '../api/excelMapping';
import type { ExcelMappingConfig } from '../api/excelMapping';
import { logMappingConfig, logProcessingUrls } from '../config/appConfig';
import { useTranslation } from 'react-i18next';

const UploadPage: React.FC = () => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<ExcelMappingConfig | null>(null);
  const [selectedConfigs, setSelectedConfigs] = useState<ExcelMappingConfig[]>([]);
  const [configs, setConfigs] = useState<ExcelMappingConfig[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [configsLoading, setConfigsLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<any>(null);
  const [existenceCheck, setExistenceCheck] = useState<{ checked: boolean; duplicates: Array<{ nif: string; exists: boolean; count: number }> }>({ checked: false, duplicates: [] });

  // Debug: Log cuando cambia el estado del modal
  useEffect(() => {
    // Modal state changed
  }, [showConfirmModal]);

  // Cargar información del entorno seleccionado
  useEffect(() => {
    const loadSelectedEnvironment = async () => {
      try {
        const activeProfileName = localStorage.getItem('activeConfigProfile');
        if (activeProfileName) {
          // Usar axiosClient para que incluya el header X-Config-Profile
          const response = await axiosClient.get(`/config-profiles/${activeProfileName}`);
          if (response.data) {
            setSelectedEnvironment(response.data);
          }
        }
      } catch (error) {
        console.error('Error al cargar entorno seleccionado:', error);
      }
    };

    loadSelectedEnvironment();

    // Escuchar cambios en el localStorage para recargar el entorno cuando cambia
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'activeConfigProfile') {
        loadSelectedEnvironment();
      }
    };

    // También verificar cambios locales periódicamente
    let lastProfile = localStorage.getItem('activeConfigProfile');
    const checkInterval = setInterval(() => {
      const currentProfile = localStorage.getItem('activeConfigProfile');
      if (currentProfile !== lastProfile) {
        lastProfile = currentProfile;
        loadSelectedEnvironment();
      }
    }, 500);

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(checkInterval);
    };
  }, []);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setConfigsLoading(true);
      const response = await excelMappingApi.getConfigs();
      if (response.success && response.data) {
        const configsData: ExcelMappingConfig[] = Array.isArray(response.data)
          ? (response.data as ExcelMappingConfig[])
          : ([response.data as ExcelMappingConfig]);
        const active: ExcelMappingConfig[] = configsData.filter((config: ExcelMappingConfig) => !!config.isActive);
        setConfigs(active);
        // Autoseleccionar la configuración por defecto si existe
        const defaultCfg = active.find((c: ExcelMappingConfig) => !!c.isDefault);
        if (defaultCfg) {
          setSelectedConfig(defaultCfg);
          setSelectedConfigs([defaultCfg]);
        }
      }
    } catch (err) {
      console.error('Error al cargar configuraciones:', err);
    } finally {
      setConfigsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setMessage('');
      setSuccess(false);
    }
  };

  const handleUpload = async () => {
    
    if (!selectedFile) {
      setMessage(t('upload.errors.noFile'));
      return;
    }

    if (!selectedConfig && selectedConfigs.length === 0) {
      setMessage(t('upload.errors.noMapping'));
      return;
    }

    // Mostrar modal de confirmación
    setShowConfirmModal(true);
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || (!selectedConfig && selectedConfigs.length === 0)) return;

    setShowConfirmModal(false);
    setLoading(true);
    setMessage(t('upload.uploading'));
    setUploadProgress(0);
    setSuccess(false);

    // Log de URLs de procesamiento
    logProcessingUrls(selectedConfig || selectedConfigs[0]);

    // Simular progreso de subida
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    const formData = new FormData();
    formData.append('excelFile', selectedFile);
    const mappingIds = (selectedConfigs.length > 0
      ? selectedConfigs
      : (selectedConfig ? [selectedConfig] : [])).map(c => c.id!);
    if (mappingIds.length > 0) {
      formData.append('mappingConfigId', String(mappingIds[0]));
    }
    formData.append('mappingConfigIds', JSON.stringify(mappingIds));
    if (existenceCheck.checked && existenceCheck.duplicates.length > 0) {
      const exclude = existenceCheck.duplicates.map(d => d.nif);
      formData.append('excludeNifs', JSON.stringify(exclude));
    }

    try {
      // Aumentar timeout para operaciones largas con Nextcloud/servicio externo
      const response = await axiosClient.post('/upload/excel', formData, { timeout: 600000 });
      
      setUploadProgress(100);
      
      if (response.data.success) {
        const { data } = response.data;
        setMessage(`¡Procesamiento exitoso! 
          • Archivo: ${data.fileName}
          • Total clientes: ${data.totalClients}
          • Procesados correctamente: ${data.successfulClients}
          • Errores: ${data.errors}
          • NIFs excluidos: ${data.excludedNifsCount || 0}`);
        setSuccess(true);
        setSelectedFile(null);
      } else {
        setMessage(`Error: ${response.data.message}`);
        setSuccess(false);
      }

    } catch (error: unknown) {
      console.error('Error durante la subida:', error);
      if (error instanceof AxiosError) {
        if (error.response?.data?.message) {
          setMessage(`Error: ${error.response.data.message}`);
        } else {
          setMessage('Error en el procesamiento del archivo. Verifique la conexión y el formato del archivo.');
        }
      } else {
        setMessage('Error en el procesamiento del archivo. Verifique la conexión y el formato del archivo.');
      }
      setSuccess(false);
    } finally {
      clearInterval(progressInterval);
      setLoading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setMessage('');
    setSuccess(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        {t('upload.title')}
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('upload.mappingConfig')}
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('upload.selectMapping')}</InputLabel>
            <Select
              multiple
              value={(selectedConfigs.length > 0 ? selectedConfigs : (selectedConfig ? [selectedConfig] : [])).map(c => c.id!)}
              onChange={(e) => {
                const values = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                const ids = values.map(v => Number(v));
                const chosen = configs.filter(c => ids.includes(c.id!));
                setSelectedConfigs(chosen);
                setSelectedConfig(chosen[0] || null);
                setMessage('');
                setSuccess(false);
                if (chosen[0]) {
                  logMappingConfig(chosen[0]);
                }
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => {
                    const cfg = configs.find(c => c.id === id);
                    return <Chip key={id} label={cfg?.name || id} size="small" />;
                  })}
                </Box>
              )}
              label={t('upload.selectMapping')}
              disabled={configsLoading}
            >
              {configs.map((config: ExcelMappingConfig) => (
                <MenuItem key={config.id} value={config.id}>
                  <Box>
                    <Typography variant="body1">{config.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {config.excelFileName} • {config.fields?.length || 0} campos
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(selectedConfigs.length > 0 || selectedConfig) && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'primary.50' }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Settings color="primary" />
                <Typography variant="subtitle1" color="primary">
                  {t('upload.selectedConfig')}: {(selectedConfigs.length > 0 ? selectedConfigs : (selectedConfig ? [selectedConfig] : [])).map(c => c.name).join(', ')}
                </Typography>
              </Box>
              {(() => { const cfg = selectedConfigs[0] || selectedConfig; return cfg ? (
                <>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {cfg.description}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {cfg.fields?.map((field, index) => (
                      <Chip
                        key={field.id || `${field.targetField}-${index}`}
                        label={`${field.targetFieldLabel}${field.isRequired ? ' *' : ''}`}
                        size="small"
                        color={field.excelColumn ? 'primary' : 'default'}
                        variant={field.excelColumn ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </>
              ) : null; })()}
            </Paper>
          )}

          <Typography variant="h6" gutterBottom>
            {t('upload.selectFile')}
          </Typography>
          
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              border: '2px dashed',
              borderColor: selectedFile ? 'primary.main' : 'grey.300',
              backgroundColor: selectedFile ? 'primary.50' : 'grey.50',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'primary.50',
              },
            }}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={loading}
              aria-label={t('upload.ariaSelectFile')}
              style={{ display: 'none' }}
            />
            
            <CloudUpload sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {selectedFile ? t('upload.fileSelected') : t('upload.clickToSelect')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('upload.supportedFormats')}
            </Typography>
          </Paper>

          {selectedFile && (
            <Paper sx={{ mt: 2, p: 2, backgroundColor: 'grey.50' }}>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <InsertDriveFile color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={selectedFile.name}
                    secondary={`${t('upload.size')}: ${formatFileSize(selectedFile.size)}`}
                  />
                  <IconButton onClick={handleRemoveFile} disabled={loading}>
                    <Delete />
                  </IconButton>
                </ListItem>
              </List>
            </Paper>
          )}

          {loading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                {t('upload.processing')} {uploadProgress}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ mt: 1 }}
              />
            </Box>
          )}

          {message && (
            <Alert 
              severity={success ? 'success' : 'error'} 
              sx={{ mt: 2 }}
              icon={success ? <CheckCircle /> : <Error />}
            >
              {message}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || (selectedConfigs.length === 0 && !selectedConfig) || loading}
              startIcon={<CloudUpload />}
              size="large"
            >
              {loading ? t('upload.processingShort') : t('upload.uploadAndProcess')}
            </Button>
            
            {selectedFile && !loading && (
              <Button
                variant="outlined"
                onClick={handleRemoveFile}
                startIcon={<Delete />}
              >
                {t('common.deleteFile')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Información adicional */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('upload.info.title')}
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <Description color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={t('upload.info.supportedTitle')}
                secondary={t('upload.info.supportedDesc')}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText
                primary={t('upload.info.autoTitle')}
                secondary={t('upload.info.autoDesc')}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemIcon>
                <Description color="info" />
              </ListItemIcon>
              <ListItemText
                primary={t('upload.info.resultsTitle')}
                secondary={t('upload.info.resultsDesc')}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Modal de confirmación */}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          {t('upload.confirm.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('upload.confirm.subtitle')}
          </DialogContentText>
          
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              📋 {t('upload.mappingConfig')}
            </Typography>
            {(() => { const cfgs = selectedConfigs.length > 0 ? selectedConfigs : (selectedConfig ? [selectedConfig] : []); const first = cfgs[0]; return (
              <>
                <Typography variant="body1" gutterBottom>
                  <strong>{t('upload.name')}:</strong> {cfgs.map(c => c.name).join(', ')}
                </Typography>
                {first && (
                  <>
                    <Typography variant="body1" gutterBottom>
                      <strong>{t('upload.description')}:</strong> {first.description}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>{t('upload.excelFile')}:</strong> {first.excelFileName}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      <strong>{t('upload.fieldsCount')}:</strong> {first.fields?.length || 0}
                    </Typography>
                  </>
                )}
              </>
            ); })()}
          </Paper>

          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'primary.50' }}>
            <Typography variant="h6" gutterBottom>
              📤 {t('upload.fileToProcess')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>{t('upload.name')}:</strong> {selectedFile?.name}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>{t('upload.size')}:</strong> {selectedFile ? formatFileSize(selectedFile.size) : ''}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, backgroundColor: 'info.50' }}>
            <Typography variant="h6" gutterBottom>
              🔗 {t('upload.processingService')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>{t('upload.externalService')}:</strong> {selectedEnvironment?.externalServiceUrl || t('upload.externalServiceBackend')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>{t('upload.environment')}:</strong> {selectedEnvironment?.name || t('upload.notSelected')}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>{t('upload.mappingConfig')}:</strong> {(selectedConfigs.length > 0 ? selectedConfigs : (selectedConfig ? [selectedConfig] : [])).map(c => c.name).join(', ')}
            </Typography>
          </Paper>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('upload.confirm.info')}
            </Typography>
          </Alert>

          {/* Comprobación de existencia por (nif, template/tipo) */}
          {selectedConfig && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={async () => {
                  try {
                    if (!selectedFile) {
                      setExistenceCheck({ checked: true, duplicates: [] });
                      return;
                    }
                    const arrayBuffer = await selectedFile.arrayBuffer();
                    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[firstSheetName];
                    const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });
                    // Determinar la columna NIF desde el mapping o por nombres comunes
                    const nifField = (selectedConfig.fields || []).find((f: any) => f.targetField === 'nif' || /\bnif\b/i.test(f.targetField));
                    const headerKeys = rows.length > 0 ? Object.keys(rows[0]) : [];
                    const candidates = [nifField?.excelColumn, 'NIF', 'nif', 'NIF/CIF', 'Grantor NIF/CIF'].filter(Boolean) as string[];
                    const pickHeader = (keys: string[], cands: string[]): string | undefined => {
                      const lowered = new Map(keys.map(k => [k.toLowerCase(), k] as const));
                      for (const c of cands) {
                        const hit = lowered.get(c.toLowerCase());
                        if (hit) return hit;
                      }
                      return undefined;
                    };
                    const nifHeader = pickHeader(headerKeys, candidates);
                    if (!nifHeader) {
                      setExistenceCheck({ checked: true, duplicates: [] });
                      return;
                    }
                    const nifs = Array.from(new Set(rows.map(r => String(r[nifHeader] || '').trim()).filter(Boolean)));
                    const template = (selectedConfig as any).reportTemplate || '';
                    const type = (selectedConfig as any).reportType || '';
                    const checks = await Promise.allSettled(
                      nifs.map(nif => excelMappingApi.existsGeneratedFor(nif, { template: template || undefined, type: template ? undefined : (type || undefined) }))
                    );
                    const duplicates = checks.map((res, idx) => ({
                      nif: nifs[idx],
                      exists: (res.status === 'fulfilled' && res.value?.data?.exists) ? true : false,
                      count: (res.status === 'fulfilled' && res.value?.data?.count) ? Number(res.value.data.count) : 0,
                    })).filter(x => x.exists);
                    setExistenceCheck({ checked: true, duplicates });
                  } catch (e) {
                    setExistenceCheck({ checked: true, duplicates: [] });
                  }
                }}
                startIcon={<Settings />}
              >
                {t('upload.checkExistence')}
              </Button>
              {existenceCheck.checked && existenceCheck.duplicates.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    {t('upload.existenceFound')}
                  </Typography>
                  <Box component="ul" sx={{ pl: 3, m: 0 }}>
                    {existenceCheck.duplicates.slice(0, 20).map(d => (
                      <li key={d.nif}><Typography variant="caption">{d.nif} ({d.count})</Typography></li>
                    ))}
                  </Box>
                  {existenceCheck.duplicates.length > 20 && (
                    <Typography variant="caption">+{existenceCheck.duplicates.length - 20} {t('common.more')}</Typography>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShowConfirmModal(false)}
            variant="outlined"
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmUpload}
            variant="contained"
            color="primary"
            startIcon={<CloudUpload />}
            disabled={existenceCheck.checked && existenceCheck.duplicates.length > 0 && existenceCheck.duplicates.length === (existenceCheck.duplicates?.length || 0)}
          >
            {existenceCheck.checked && existenceCheck.duplicates.length > 0
              ? t('upload.confirm.excludeAndGenerate')
              : t('upload.confirm.cta')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadPage;